import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import {
  faArrowRotateRight,
  faArrowsRotate,
  faCircleXmark,
  faDownload,
  faExternalLink,
} from '@fortawesome/pro-regular-svg-icons';

import { AsyncFunction, Job as JobType, Payload } from '@typings';
import { toast } from '@features';
import { createJob, killRunningJob } from '@services';
import { userSelector } from '@selectors';
import { useSelector } from '@hooks';
import { as, invariant, path, pick, toastifyResponseError } from '@utils';

import { Button, Link, Modal } from '@components';
import { SaveJobImageModal } from '@components/Modals';
import { BadgeStatus } from '@components/Ui';

type Loading = {
  isKilling: boolean;
  isRefreshing: boolean;
  isRerunning: boolean;
};

type Props = {
  job: JobType.Model | null;
  getJob: AsyncFunction;
};

export const JobDetailsHandleCenter = ({ job, getJob }: Props) => {
  const { username } = useSelector(userSelector);

  const navigate = useNavigate();

  const [loading, setLoading] = useImmer<Loading>({
    isKilling: false,
    isRefreshing: false,
    isRerunning: false,
  });

  const {
    id: jobId,
    owner,
    status,
    httpUrl,
    httpUrlNamed,
    history,
  } = as.o<JobType.Model>(job);
  const isRunningJob = ['pending', 'running'].includes(status);
  const isJobFiled = ['cancelled', 'failed'].includes(status);
  const isOpenLinkDisabled = isJobFiled || (!httpUrlNamed && !httpUrl);
  const isJobRerunDisabled =
    owner !== username || !username || isRunningJob || !job;

  const handleLoading = useCallback(
    (name: keyof Loading, value: boolean) => {
      setLoading((state) => {
        state[name] = value;
      });
    },
    [setLoading],
  );

  const handleJobRefresh = useCallback(async () => {
    try {
      handleLoading('isRefreshing', true);

      await getJob();
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      handleLoading('isRefreshing', false);
    }
  }, [handleLoading, getJob]);

  const handleJobKill = async () => {
    try {
      handleLoading('isKilling', true);

      await killRunningJob(jobId);
      await getJob();
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      handleLoading('isKilling', false);
    }
  };

  const handleJobRerun = async () => {
    try {
      handleLoading('isRerunning', true);

      invariant(job);

      const {
        orgName: organizationName,
        maxRunTimeMinutes: lifespanMinutes,
        privileged: privilegedMode,
        ...jobPick
      } = pick(
        job,
        'clusterName',
        'projectName',
        'presetName',
        'name',
        'tags',
        'description',
        'orgName',
        'restartPolicy',
        'scheduleTimeout',
        'maxRunTimeMinutes',
        'privileged',
      );
      const {
        /**
         * todo: fix terminal error
         */
        // @ts-ignore
        http: { port: httpPort, requiresAuth: httpAuth } = {},
        tty: ttyAllocation,
        volumes,
        diskVolumes,
        secretVolumes,
        ...jobContainerPick
      } = pick(
        job.container,
        'command',
        'entrypoint',
        'env',
        'image',
        'diskVolumes',
        'secretEnv',
        'secretVolumes',
        'http',
        'tty',
        'diskVolumes',
        'volumes',
        'secretVolumes',
      );

      const jobVolumes: Payload.CreateJob['jobVolumes'] = {
        secretVolumes,
        volumes,
        diskVolumes,
      };
      const payload = {
        organizationName,
        httpPort,
        httpAuth,
        ttyAllocation,
        lifespanMinutes,
        privilegedMode,
        jobVolumes,
        ...jobPick,
        ...jobContainerPick,
      };

      const { id: jobId } = await createJob(payload);
      await getJob();

      toast.success(`Job ${job.name ?? ''} rerun`);

      navigate(path.job(jobId));
    } catch (error) {
      toastifyResponseError(error);
    } finally {
      handleLoading('isRerunning', false);
    }
  };

  return (
    <div className="mb-8 flex justify-end gap-2">
      <BadgeStatus {...history} variant="details" />
      <Link
        blank
        external
        theme
        to={httpUrlNamed ?? httpUrl}
        disabled={isOpenLinkDisabled}
        className="ml-auto"
      >
        <Button
          variant="rebecca"
          disabled={isOpenLinkDisabled}
          className="flex items-center gap-1.5 px-6"
          icon={faExternalLink}
        >
          Open
        </Button>
      </Link>
      <Modal
        disabled={status !== 'running'}
        content={<SaveJobImageModal jobId={jobId} />}
      >
        <Button
          variant="rebecca"
          disabled={status !== 'running'}
          className="flex items-center gap-1.5 capitalize"
          icon={faDownload}
        >
          Save image
        </Button>
      </Modal>
      <Button
        variant="rebecca"
        loading={loading.isRefreshing}
        disabled={!job}
        icon={faArrowsRotate}
        className="flex items-center gap-1.5"
        onClick={handleJobRefresh}
      >
        Refresh
      </Button>
      <Button
        variant="rebecca"
        loading={loading.isRerunning}
        disabled={isJobRerunDisabled}
        icon={faArrowRotateRight}
        onClick={handleJobRerun}
      >
        Rerun
      </Button>
      <Button
        variant="rebecca"
        loading={loading.isKilling}
        disabled={!isRunningJob}
        icon={faCircleXmark}
        className="text-error"
        onClick={handleJobKill}
      >
        Kill
      </Button>
    </div>
  );
};
